// Placeholder-token fill for templated Excel exports.
//
// DG templates carry bracketed tokens in their header/summary text —
// "[Address]", "[Site #]", "Total: [Building SF] (Sales: [Sales SF] …)" —
// including inside composed strings that per-cell mappings can't target.
// Instead of authoring excel_cell_mappings for these, the export scans every
// sheet for known tokens and substitutes property info and survey answers in
// place. Unknown tokens ("[Finding]", roofing rows…) are left untouched, and
// a cell is only rewritten when at least one token resolved.

import type { CellWrite } from "./xlsxTemplatePatcher.ts";
import { listXlsxSheetNames, readSheetCells } from "./xlsxTemplatePatcher.ts";
import type { ExportPropertyInfo } from "./exportCore.ts";
import { SurveyAnswers } from "./gcEvaluationFill.ts";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function dateParts(value: string | null | undefined): { y: string; m: number; d: number } | null {
  const m = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? { y: m[1], m: Number(m[2]), d: Number(m[3]) } : null;
}

const fmt = (n: number) => n.toLocaleString("en-US");
const sf = (n: number | null) => (n === null ? null : `${fmt(n)} SF`);

export interface TokenContext {
  property?: ExportPropertyInfo | null;
  capturedAt?: string | null;
  answers: SurveyAnswers;
}

/** Token → replacement text; null values leave the token in place. */
export function buildTokenValues(ctx: TokenContext): Record<string, string | null> {
  const { property, capturedAt, answers: a } = ctx;
  const cityState = [property?.city, property?.state].filter(Boolean).join(", ") || null;
  const date = dateParts(capturedAt);
  const pad = (n: number) => String(n).padStart(2, "0");

  // Question titles drift between survey templates — try known variants.
  const num = (...titles: string[]) =>
    titles.map((t) => a.num(t)).find((v) => v !== null) ?? null;
  const text = (...titles: string[]) =>
    titles.map((t) => a.text(t)).find((v) => v !== null) ?? null;

  const building = num("Total Building SF", "Building SF", "Total SF");
  const sales = num("Sales Floor SF", "Sales SF", "Sales Floor Square Footage");
  const recv = num("Receiving Room SF", "Receiving SF", "Receiving Room Square Footage");
  const lot = num("Parking Lot SF", "Lot SF", "Parking Lot Square Footage");
  const zone = text("Climate Zone", "Zone");
  const required = num(
    "Required tonnage per zone (Building SF ÷ Zone SF/Ton, round up to 0.5t)",
    "Required tonnage per zone",
    "Min. Tonnage Required"
  );
  const deficit = num("Tonnage deficit / add", "Tonnage deficit");
  const damaged = num("% of parking lot with damaged pavement");
  const leaks = num("Number of active roof leaks (per manager)", "Number of active roof leaks");
  const storeType = text("Store Type (Based off of FY27)", "Store Type");

  return {
    "Address": property?.address ?? null,
    "City, State ZIP": cityState ? [cityState, property?.zip_code].filter(Boolean).join("  ") : null,
    "City, State": cityState,
    "Site #": property?.store_number ? `Site #${property.store_number}` : null,
    "Date": date ? `${pad(date.m)}.${pad(date.d)}.${date.y}` : null,
    "Survey Date": date ? `${MONTHS[date.m - 1]} ${date.d}, ${date.y}` : null,
    "Store Type": storeType,
    "Building SF": sf(building),
    "Sales SF": sf(sales),
    "Receiving SF": sf(recv),
    "Lot SF": sf(lot),
    "Zone": zone,
    "Tonnage": required !== null ? `${required} tons` : null,
    "Sales / Receiving SF":
      sales !== null && recv !== null ? `Sales ${fmt(sales)} · Recv ${fmt(recv)}` : null,
    "Tonnage deficit":
      deficit === null
        ? null
        : deficit > 0
          ? `${deficit}T deficit${zone ? ` (Zone ${zone})` : ""}`
          : `No deficit${zone ? ` (Zone ${zone})` : ""}`,
    "Pavement condition": damaged !== null ? `${damaged}% damaged pavement` : null,
    "Roof note":
      leaks !== null
        ? `${leaks} active leak${leaks === 1 ? "" : "s"}  ·  Pending GC eval`
        : null,
  };
}

/** Pure core: cell texts × token values → rewritten-cell writes. */
export function buildTokenWrites(
  sheetName: string,
  cells: Map<string, string>,
  tokens: Record<string, string | null>
): CellWrite[] {
  const writes: CellWrite[] = [];
  for (const [ref, text] of cells) {
    if (!text.includes("[")) continue;
    let replaced = text;
    for (const [token, value] of Object.entries(tokens)) {
      if (value !== null) replaced = replaced.split(`[${token}]`).join(value);
    }
    if (replaced !== text) writes.push({ sheet: sheetName, cell: ref, value: replaced });
  }
  return writes;
}

/** Token writes across every sheet of the workbook. */
export async function collectTokenWrites(
  templateBuf: ArrayBuffer,
  ctx: TokenContext
): Promise<CellWrite[]> {
  const tokens = buildTokenValues(ctx);
  if (Object.values(tokens).every((v) => v === null)) return [];
  const writes: CellWrite[] = [];
  for (const sheet of await listXlsxSheetNames(templateBuf)) {
    const cells = await readSheetCells(templateBuf, sheet);
    writes.push(...buildTokenWrites(sheet, cells, tokens));
  }
  return writes;
}
